import { z } from 'zod';

export const workflowTemplateSchema = z.object({
  name: z.string().min(1, 'Naziv je obavezan'),
  description: z.string().optional(),
});

export type WorkflowTemplateFormData = z.infer<typeof workflowTemplateSchema>;

/** The backend's machine-key rule for a step field. */
const FIELD_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;

const isPositiveInteger = (text: string) => /^[1-9]\d*$/.test(text.trim());

/**
 * Optional whole hours typed as text, the backend's `@IsInt() @Min(1)`. The
 * inputs carry no native `min`, so this Serbian message is the only one shown.
 */
const optionalHours = (message: string) =>
  z
    .string()
    .optional()
    .refine((text) => !text?.trim() || isPositiveInteger(text), message);

/**
 * The stored value of a CHOICE option. The builder only asks for the label:
 * a value left empty is derived from it ("Premiere projekat" → "premiere-projekat"),
 * so nobody has to invent a machine value or read an English validation error.
 */
export function stepFieldOptionValue(option: { value?: string; label: string }): string {
  const typed = option.value?.trim();
  if (typed) return typed;
  return option.label
    .trim()
    .toLowerCase()
    .replace(/đ/g, 'd')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** One configurable step field as edited in the builder; hours travel as text. */
const stepFieldOptionSchema = z.object({
  value: z.string().optional(),
  label: z.string().trim().min(1, 'Naziv opcije je obavezan'),
  durationHours: optionalHours('Sati moraju biti ceo broj, najmanje 1'),
});

const stepFieldSchema = z
  .object({
    key: z
      .string()
      .trim()
      .min(1, 'Ključ polja je obavezan')
      .regex(FIELD_KEY_PATTERN, 'Ključ: mala slova, cifre, "-" i "_" (najviše 64 znaka)'),
    label: z.string().trim().min(1, 'Naziv polja je obavezan'),
    type: z.enum(['TEXT', 'TEXTAREA', 'TAGS', 'CHOICE', 'CHECKBOX', 'DATETIME', 'FILES']),
    phase: z.enum(['ACCEPT', 'COMPLETE']),
    required: z.boolean(),
    options: z.array(stepFieldOptionSchema).optional(),
  })
  // The backend's rules for a choice, answered here on the input that breaks them.
  .superRefine((field, ctx) => {
    if (field.type !== 'CHOICE') return;
    const options = field.options ?? [];
    if (options.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['options'],
        message: 'Izbor mora da ima bar jednu opciju',
      });
    }
    const seen = new Set<string>();
    options.forEach((option, index) => {
      const value = stepFieldOptionValue(option);
      if (!value) {
        // A label with nothing to derive a value from ("???"); an empty label
        // already has its own error.
        if (option.label.trim()) {
          ctx.addIssue({
            code: 'custom',
            path: ['options', index, 'value'],
            message: 'Unesite vrednost opcije',
          });
        }
        return;
      }
      if (seen.has(value)) {
        ctx.addIssue({
          code: 'custom',
          path: ['options', index, 'value'],
          message: 'Vrednost opcije mora biti jedinstvena',
        });
      }
      seen.add(value);
    });
  });

/** Field keys are unique within a step; the duplicate is flagged on its own key input. */
const stepFieldsSchema = z.array(stepFieldSchema).superRefine((fields, ctx) => {
  const seen = new Set<string>();
  fields.forEach((field, index) => {
    const key = field.key.trim();
    if (key && seen.has(key)) {
      ctx.addIssue({
        code: 'custom',
        path: [index, 'key'],
        message: 'Ključ polja mora biti jedinstven',
      });
    }
    seen.add(key);
  });
});

export const workflowStepSchema = z
  .object({
    name: z.string().min(1, 'Naziv koraka je obavezan'),
    executorKind: z.enum(['ROLE', 'EPISODE_PM']),
    executorRoleId: z.string().optional(),
    assignerRoleId: z.string().optional(),
    parallelGroupId: z.string().optional(),
    isLoopable: z.boolean().optional(),
    defaultDurationHours: optionalHours('Trajanje mora biti ceo broj sati, najmanje 1'),
    acceptWithinHours: optionalHours('Rok za prihvatanje mora biti ceo broj sati, najmanje 1'),
    notifyOnApprove: z.boolean().optional(),
    notifyOnReject: z.boolean().optional(),
    notifyApproveRoles: z.array(z.string()).optional(),
    notifyRejectRoles: z.array(z.string()).optional(),
    notifyOnAccept: z.boolean().optional(),
    notifyAcceptRoles: z.array(z.string()).optional(),
    notifyPm: z.boolean().optional(),
    instructions: z.string().optional(),
    rejectionReasonsText: z.string().optional(),
    checklistItemsText: z.string().optional(),
    groupId: z.string().nullable().optional(),
    schedulingType: z.string().optional(),
    dependsOnStepIds: z.array(z.string()).optional(),
    fields: stepFieldsSchema,
  })
  // A role-executed step needs its role; a PM-executed one needs none (TASK-48).
  .superRefine((data, ctx) => {
    if (data.executorKind === 'ROLE' && !data.executorRoleId) {
      ctx.addIssue({
        code: 'custom',
        path: ['executorRoleId'],
        message: 'Izaberite ulogu izvršioca',
      });
    }
  });

export type WorkflowStepFormData = z.infer<typeof workflowStepSchema>;

export const workflowStepGroupSchema = z.object({
  name: z.string().min(1, 'Naziv faze je obavezan'),
  isParallel: z.boolean().optional(),
});

export type WorkflowStepGroupFormData = z.infer<typeof workflowStepGroupSchema>;
